<template>
  <section class="max-w-xl mx-auto px-4 space-y-6">
    <!-- Heading -->
    <div>
      <p>{{ $t("domain.subtitle") }}</p>
    </div>

    <!-- Input + Actions -->
    <div class="flex flex-col gap-2">
      <div class="relative flex items-center">
        <input
          id="domain-input"
          v-model="domainInput"
          @input="fetchSuggestions"
          @keydown.enter.prevent="checkDomain"
          type="text"
          :placeholder="$t('domain.placeholder')"
          class="input input-bordered flex-1 focus:ring-2 focus:ring-primary transition"
          :aria-invalid="messageType === 'error'"
          aria-describedby="domain-msg" />
        <button @click="randomize" class="btn btn-outline ml-2 p-2" aria-label="Random domain suggestion">🔄</button>
      </div>
      <ul v-if="suggestions.length" class="flex gap-2 overflow-x-auto text-sm">
        <li v-for="s in suggestions" :key="s">
          <button @click="applySuggestion(s)" class="btn btn-sm btn-ghost whitespace-nowrap">
            {{ s }}
          </button>
        </li>
      </ul>
    </div>

    <!-- Status Message -->
    <div v-if="message" role="status" aria-live="polite" id="domain-msg">
      <div :class="alertClasses">{{ message }}</div>
    </div>

    <!-- Conditional Steps (only show if not in final state) -->
    <div v-if="!isFlowComplete">
      <div v-if="flowState === 'taken-other' && ownsDomain === null" class="card border border-base-300 bg-base-100">
        <div class="card-body">
          <h3 class="text-base font-semibold">{{ $t("domain.askOwnership.title") }}</h3>
          <div class="flex flex-col sm:flex-row gap-2">
            <button class="btn btn-outline" @click="setOwnsDomain(true)">{{ $t("domain.askOwnership.yes") }}</button>
            <button class="btn btn-outline" @click="setOwnsDomain(false)">{{ $t("domain.askOwnership.no") }}</button>
          </div>
        </div>
      </div>

      <!-- Transfer / Keep -->
      <div v-if="flowState === 'taken-own-manage'" class="card border border-base-300 bg-base-100">
        <div class="card-body">
          <h3 class="text-base font-semibold">{{ $t("domain.transfer.title") }}</h3>
          <p class="text-sm text-base-content/70">{{ $t("domain.transfer.subtitle") }}</p>
          <div class="flex flex-col sm:flex-row gap-2">
            <button class="btn btn-primary" @click="confirmTransfer">{{ $t("domain.transfer.transfer") }}</button>
            <button class="btn btn-outline" @click="confirmKeep">{{ $t("domain.transfer.keep") }}</button>
          </div>
        </div>
      </div>

      <!-- Not Owned Skip -->
      <div v-if="flowState === 'taken-own-keep'" class="card border border-base-300 bg-base-100">
        <div class="card-body">
          <h3 class="text-base font-semibold">{{ $t("domain.notOwned.title") }}</h3>
          <p class="text-sm text-base-content/70">{{ $t("domain.notOwned.subtitle") }}</p>
          <div class="flex flex-col sm:flex-row gap-2">
            <button class="btn btn-outline" @click="reset">🔄 {{ $t("domain.notOwned.tryAnother") }}</button>
            <button class="btn btn-primary" @click="skip">{{ $t("domain.notOwned.skip") }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Check/Restart Button -->
    <button
      class="btn w-full flex justify-center items-center"
      :class="isFlowComplete ? 'btn-outline' : 'btn-primary'"
      @click="isFlowComplete ? reset() : checkDomain()"
      :disabled="checking">
      <span v-if="!checking && !isFlowComplete">{{ $t("domain.check") }}</span>
      <span v-else-if="!checking && isFlowComplete">🔄 {{ $t("domain.restart") }}</span>
      <span v-else class="loading loading-spinner" aria-label="Checking…"></span>
    </button>

    <!-- Optional Note (only show when not in final state) -->
    <p v-if="!isFlowComplete" class="text-center text-sm text-base-content/70">{{ $t("domain.optionalNote") }}</p>

    <!-- Final State Summary Cards -->
    <div v-if="isFlowComplete" class="mt-4">
      <!-- Available Domain -->
      <div v-if="flowState === 'available'" class="card bg-green-50 border border-green-200">
        <div class="card-body p-4">
          <div class="flex items-start gap-3">
            <div class="text-2xl">🎉</div>
            <div class="flex-1">
              <h4 class="font-semibold text-green-800 mb-1">{{ $t("domain.finalStates.available.title") }}</h4>
              <p class="text-sm text-green-700 mb-2">
                {{ $t("domain.finalStates.available.subtitle", { domain: domainName }) }}
              </p>
              <ul class="text-xs text-green-600 space-y-1">
                <li>• {{ $t("domain.finalStates.available.included1") }}</li>
                <li>• {{ $t("domain.finalStates.available.included2") }}</li>
                <li>• {{ $t("domain.finalStates.available.included3") }}</li>
              </ul>
              <div class="text-xs font-medium text-green-800 mt-2">{{ $t("domain.finalStates.available.price") }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Domain Transfer -->
      <div v-if="flowState === 'taken-own-manage'" class="card bg-blue-50 border border-blue-200">
        <div class="card-body p-4">
          <div class="flex items-start gap-3">
            <div class="text-2xl">🔄</div>
            <div class="flex-1">
              <h4 class="font-semibold text-blue-800 mb-1">{{ $t("domain.finalStates.transfer.title") }}</h4>
              <p class="text-sm text-blue-700 mb-2">
                {{ $t("domain.finalStates.transfer.subtitle", { domain: domainName }) }}
              </p>
              <ul class="text-xs text-blue-600 space-y-1">
                <li>• {{ $t("domain.finalStates.transfer.included1") }}</li>
                <li>• {{ $t("domain.finalStates.transfer.included2") }}</li>
                <li>• {{ $t("domain.finalStates.transfer.included3") }}</li>
              </ul>
              <div class="text-xs font-medium text-blue-800 mt-2">{{ $t("domain.finalStates.transfer.timeline") }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Keep External Domain -->
      <div v-if="flowState === 'taken-own-keep'" class="card bg-amber-50 border border-amber-200">
        <div class="card-body p-4">
          <div class="flex items-start gap-3">
            <div class="text-2xl">🔗</div>
            <div class="flex-1">
              <h4 class="font-semibold text-amber-800 mb-1">{{ $t("domain.finalStates.keep.title") }}</h4>
              <p class="text-sm text-amber-700 mb-2">
                {{ $t("domain.finalStates.keep.subtitle", { domain: domainName || "your domain" }) }}
              </p>
              <ul class="text-xs text-amber-600 space-y-1">
                <li>• {{ $t("domain.finalStates.keep.included1") }}</li>
                <li>• {{ $t("domain.finalStates.keep.included2") }}</li>
                <li>• {{ $t("domain.finalStates.keep.included3") }}</li>
              </ul>
              <div class="text-xs font-medium text-amber-800 mt-2">{{ $t("domain.finalStates.keep.cost") }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Deferred Domain -->
      <div v-if="flowState === 'deferred'" class="card bg-gray-50 border border-gray-200">
        <div class="card-body p-4">
          <div class="flex items-start gap-3">
            <div class="text-2xl">⏭️</div>
            <div class="flex-1">
              <h4 class="font-semibold text-gray-800 mb-1">{{ $t("domain.finalStates.deferred.title") }}</h4>
              <p class="text-sm text-gray-700 mb-2">{{ $t("domain.finalStates.deferred.subtitle") }}</p>
              <ul class="text-xs text-gray-600 space-y-1">
                <li>• {{ $t("domain.finalStates.deferred.included1") }}</li>
                <li>• {{ $t("domain.finalStates.deferred.included2") }}</li>
                <li>• {{ $t("domain.finalStates.deferred.included3") }}</li>
              </ul>
              <div class="text-xs font-medium text-gray-800 mt-2">{{ $t("domain.finalStates.deferred.cost") }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Deferred with Consultation -->
      <div v-if="flowState === 'deferred-with-consultation'" class="card bg-purple-50 border border-purple-200">
        <div class="card-body p-4">
          <div class="flex items-start gap-3">
            <div class="text-2xl">💬</div>
            <div class="flex-1">
              <h4 class="font-semibold text-purple-800 mb-1">{{ $t("domain.finalStates.consultation.title") }}</h4>
              <p class="text-sm text-purple-700 mb-2">{{ $t("domain.finalStates.consultation.subtitle") }}</p>
              <ul class="text-xs text-purple-600 space-y-1">
                <li>• {{ $t("domain.finalStates.consultation.included1") }}</li>
                <li>• {{ $t("domain.finalStates.consultation.included2") }}</li>
                <li>• {{ $t("domain.finalStates.consultation.included3") }}</li>
              </ul>
              <div class="text-xs font-medium text-purple-800 mt-2">
                {{ $t("domain.finalStates.consultation.cost") }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <p class="text-xs text-base-content/60">{{ $t("domain.footer") }}</p>
  </section>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import WhmcsApiService from "@/services/WhmcsApiService";
import { useDomainSelection } from "@/features/domain-checker-input/composables/useDomainSelection";

const store = useDomainSelection();
const { flowState, domainName } = storeToRefs(store);
const domainInput = ref("");
const checking = ref(false);
const message = ref("");
const messageType = ref<"success" | "error" | "warning" | "">("");
const suggestions = ref<string[]>([]);
const ownsDomain = ref<boolean | null>(null);
const transferDecisionMade = ref(false);

const alertClasses = computed(() => [
  "alert",
  messageType.value === "success" ? "alert-success" : messageType.value === "error" ? "alert-error" : "alert-warning",
]);

// Check if the flow is in a final completed state
const isFlowComplete = computed(() => {
  return (
    flowState.value === "available" ||
    flowState.value === "taken-own-keep" ||
    flowState.value === "deferred" ||
    flowState.value === "deferred-with-consultation" ||
    (flowState.value === "taken-own-manage" && transferDecisionMade.value)
  );
});

function fetchSuggestions() {
  const base = domainInput.value.trim();
  suggestions.value = base ? [`${base}.com`, `${base}.nl`] : [];
}

function randomize() {
  const opts = ["sparkly.nl", "yourbiz.app", "goonline.com"];
  domainInput.value = opts[Math.floor(Math.random() * opts.length)];
  fetchSuggestions();
}

function applySuggestion(domain: string) {
  domainInput.value = domain;
  suggestions.value = [];
}

function showMessage(text: string, type: typeof messageType.value) {
  message.value = text;
  messageType.value = type;
}

function clearMessage() {
  message.value = "";
  messageType.value = "";
}

function validateDomain(domain: string): { isValid: boolean; error?: string } {
  const trimmedDomain = domain.trim();

  // Check if domain is empty
  if (!trimmedDomain) {
    return { isValid: false, error: "Please enter a domain name." };
  }

  // Check for basic domain format
  const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.([a-zA-Z]{2,}|[a-zA-Z]{2,}\.[a-zA-Z]{2,})$/;

  if (!domainRegex.test(trimmedDomain)) {
    // Check specific common issues and provide helpful messages
    if (!trimmedDomain.includes(".")) {
      return {
        isValid: false,
        error: "Domain name must include an extension (like .com, .nl, .org). Try adding '.com' to your domain name.",
      };
    }

    if (trimmedDomain.startsWith(".") || trimmedDomain.endsWith(".")) {
      return {
        isValid: false,
        error: "Domain name cannot start or end with a dot. Please enter a valid domain like 'example.com'.",
      };
    }

    if (trimmedDomain.includes("..")) {
      return {
        isValid: false,
        error: "Domain name cannot contain consecutive dots. Please check your domain name.",
      };
    }

    if (trimmedDomain.includes(" ")) {
      return {
        isValid: false,
        error: "Domain name cannot contain spaces. Please remove any spaces from your domain name.",
      };
    }

    if (trimmedDomain.length < 3) {
      return {
        isValid: false,
        error: "Domain name is too short. Please enter a valid domain name with at least 3 characters.",
      };
    }

    // Generic invalid format message
    return {
      isValid: false,
      error: "Please enter a valid domain name (like example.com or yoursite.nl).",
    };
  }

  return { isValid: true };
}

async function checkDomain() {
  // Validate domain format first
  const validation = validateDomain(domainInput.value);
  if (!validation.isValid) {
    store.reset(); // Reset store to clear any previous domain data
    ownsDomain.value = null; // Reset ownership state
    return showMessage(validation.error!, "error");
  }

  checking.value = true;
  clearMessage();

  // Reset ownership state before making the API call
  ownsDomain.value = null;

  try {
    const dto = await WhmcsApiService.checkDomainAvailability(domainInput.value.trim());

    store.setFromDto(dto);

    if (dto.available) {
      showMessage(`${dto.domainName} is available ✅`, "success");
    } else {
      showMessage(`${dto.domainName} is already taken ❌`, "error");
    }
    ownsDomain.value = dto.userOwns ?? null;
  } catch (error) {
    // Reset domain state on API errors too
    store.reset();
    ownsDomain.value = null;
    showMessage("Something went wrong while checking the domain. Please try again.", "error");
  } finally {
    checking.value = false;
  }
}

function setOwnsDomain(value: boolean) {
  clearMessage();
  ownsDomain.value = value;

  // Update the store with the ownership information to trigger correct flow state
  store.setFromDto({
    domainName: domainName.value || domainInput.value.trim(),
    available: false,
    userOwns: value,
    transferRecommended: value ? null : true, // If user owns: ask about transfer, if not: recommend transfer
  });
}

function confirmTransfer() {
  store.setFromDto({
    domainName: domainName.value,
    available: false,
    userOwns: true,
    transferRecommended: true,
  });
  transferDecisionMade.value = true;
  clearMessage(); // Clear the message since we're showing final state card
}

function confirmKeep() {
  store.setFromDto({
    domainName: domainName.value,
    available: false,
    userOwns: true,
    transferRecommended: false,
  });
  transferDecisionMade.value = true;
  clearMessage(); // Clear the message since we're showing final state card
}

function skip() {
  store.defer(false);
  showMessage(`Skipped domain selection.`, "warning");
}

function reset() {
  store.reset();
  domainInput.value = "";
  clearMessage();
  ownsDomain.value = null;
  transferDecisionMade.value = false;
}
</script>

<style scoped>
.loading-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.5s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
